import { useEffect, useState } from "react";
import { auth } from "../firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";

export default function useAuth() {
  const [user, setUser] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [orgId, setOrgId] = useState(null);
  const [role, setRole] = useState(null);
  const [orgType, setOrgType] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [email, setEmail] = useState(
    () => localStorage.getItem("savedEmail") || ""
  );
  const [password, setPassword] = useState("");
  const [saveUsername, setSaveUsername] = useState(
    () => !!localStorage.getItem("savedEmail")
  );
  const [keepLoggedIn, setKeepLoggedIn] = useState(
    () => localStorage.getItem("keepLoggedIn") === "true"
  );
  const [authError, setAuthError] = useState("");

  useEffect(() => {
    const persistedKeep =
      localStorage.getItem("keepLoggedIn") === "true";
    const persistence = persistedKeep
      ? browserLocalPersistence
      : browserSessionPersistence;

    setPersistence(auth, persistence).catch((error) =>
      console.error("Error setting auth persistence:", error)
    );

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (
        currentUser &&
        !persistedKeep &&
        sessionStorage.getItem("authSessionActive") !== "true"
      ) {
        await signOut(auth);
        setUser(null);
        setIsOwner(false);
        setAuthLoading(false);
        return;
      }

      if (currentUser) {
        try {
          // forceRefresh = true so we always read the latest server-side
          // custom claims. Without it, getIdTokenResult returns the
          // cached token (refreshed once per hour by Firebase), which
          // means claim changes on the server — including the addition
          // of new claim keys like orgType — silently don't propagate
          // until the cache expires. Recurring "orgType=null on existing
          // users" bug came from this.
          const tokenResult = await currentUser.getIdTokenResult(true);
          setIsOwner(!!tokenResult.claims.owner);
          setOrgId(tokenResult.claims.orgId || null);
          setRole(tokenResult.claims.role || null);
          setOrgType(tokenResult.claims.orgType || null);
        } catch (err) {
          console.warn("Could not read custom claims", err);
          setIsOwner(false);
          setOrgId(null);
          setRole(null);
          setOrgType(null);
        }
      } else {
        setIsOwner(false);
        setOrgId(null);
        setRole(null);
        setOrgType(null);
      }

      setUser(currentUser);
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const applyLoginPreferences = () => {
    if (saveUsername) {
      localStorage.setItem("savedEmail", email);
    } else {
      localStorage.removeItem("savedEmail");
    }
    localStorage.setItem("keepLoggedIn", keepLoggedIn ? "true" : "false");
    sessionStorage.setItem("authSessionActive", "true");
    if (!saveUsername) setEmail("");
    setPassword("");
  };

  const handleLogin = async () => {
    try {
      setAuthError("");
      await setPersistence(
        auth,
        keepLoggedIn ? browserLocalPersistence : browserSessionPersistence
      );
      // Set the session marker BEFORE signing in. onAuthStateChanged can
      // fire as soon as signIn resolves; if the marker isn't set yet, the
      // boot-time auto-logout check trips and signs the user right back out.
      sessionStorage.setItem("authSessionActive", "true");
      await signInWithEmailAndPassword(auth, email, password);
      applyLoginPreferences();
    } catch (error) {
      sessionStorage.removeItem("authSessionActive");
      setAuthError(error.message);
    }
  };

  const handleRegister = async () => {
    try {
      setAuthError("");
      await setPersistence(
        auth,
        keepLoggedIn ? browserLocalPersistence : browserSessionPersistence
      );
      sessionStorage.setItem("authSessionActive", "true");
      await createUserWithEmailAndPassword(auth, email, password);
      applyLoginPreferences();
    } catch (error) {
      sessionStorage.removeItem("authSessionActive");
      setAuthError(error.message);
    }
  };

  const handleLogout = async () => {
    sessionStorage.removeItem("authSessionActive");
    await signOut(auth);
  };

  const refreshOwnerClaim = async () => {
    if (!auth.currentUser) return false;
    await auth.currentUser.getIdToken(true);
    const tokenResult = await auth.currentUser.getIdTokenResult();
    const next = !!tokenResult.claims.owner;
    setIsOwner(next);
    setOrgId(tokenResult.claims.orgId || null);
    setRole(tokenResult.claims.role || null);
    setOrgType(tokenResult.claims.orgType || null);
    return next;
  };

  return {
    user,
    isOwner,
    orgId,
    role,
    orgType,
    refreshOwnerClaim,
    authLoading,
    email,
    setEmail,
    password,
    setPassword,
    saveUsername,
    setSaveUsername,
    keepLoggedIn,
    setKeepLoggedIn,
    authError,
    handleLogin,
    handleRegister,
    handleLogout,
  };
}
